import { Avatar, Box, Paper, Stack, Typography, useTheme } from "@mui/material";
import { tokens } from "../../../../../../theme";
import InfoRow from "./InfoRow";
import type { BookingData } from "../../../../types/view-booking";
import { PersonOutline } from "@mui/icons-material";

interface BookingInfoCardProps {
  bookingData?: BookingData | null;
}


const parseDDMMYYYY = (value?: string) => {
  if (!value) return null;
  const [dd, mm, yyyy] = value.split("-").map((x) => Number(x));
  if (!dd || !mm || !yyyy) return null;
  const d = new Date(yyyy, mm - 1, dd);
  if (
    d.getFullYear() !== yyyy ||
    d.getMonth() !== mm - 1 ||
    d.getDate() !== dd
  ) return null;
  return d;
};

const formatPrettyDateDDMMYYYY = (value?: string) => {
  const d = parseDDMMYYYY(value);
  if (!d) return "N/A";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(d);
};

// const calculateAgeAtDeathDDMMYYYY = (dob?: string, dod?: string) => {
//   const birth = parseDDMMYYYY(dob);
//   const death = parseDDMMYYYY(dod);
//   if (!birth || !death) return null;
//   let age = death.getFullYear() - birth.getFullYear();
//   const m = death.getMonth() - birth.getMonth();
//   if (m < 0 || (m === 0 && death.getDate() < birth.getDate())) age -= 1;
//   return age >= 0 ? age : null;
// };



const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography sx={{ fontWeight: 600, fontSize: 16, color: "text.secondary", mb: 2 }}>
    {children}
  </Typography>
);

const DateBlock = ({ label, value }: { label: string; value: string }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
  <Box
    sx={{
      flex: 1,
      bgcolor: colors.primary[900],
      borderRadius: "10px",
      p: 1.5,
      border: `1px solid ${colors.primary[300]}`,
    }}
  >
    <Typography sx={{ fontSize: 10, color: "text.secondary", fontWeight: 700 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>
      {value}
    </Typography>
  </Box>
  );
};



function FuneralView({ bookingData }: { bookingData: BookingData }) {
  const { beloved } = bookingData;
  const details = beloved?.details ?? {};

  const theme = useTheme();
  const colors = tokens(theme.palette.mode)

  const dob = details.dateOfBirth;
  const dod = details.dateOfDeath;
  const sunrise = formatPrettyDateDDMMYYYY(dob);
  const sunset = formatPrettyDateDDMMYYYY(dod);
  //const age = calculateAgeAtDeathDDMMYYYY(dob, dod);

  return (
    <>
      <SectionLabel>Deceased Information</SectionLabel>

      <Box
        sx={{ bgcolor: "background.paper", borderRadius: "12px", p: 2, mb: 2 }}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5 }}>
          <Avatar
            src={beloved?.avatar || undefined}
            alt={beloved?.name || "Deceased"}
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: "#cbd5e1",
              fontWeight: 800,
            }}
          >
            <PersonOutline
              sx={{ fontSize: 48, color: colors.primary[400] }}
            />
          </Avatar>

          <Box>
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: 18,
                color: "#0f172a",
                lineHeight: 1.2,
              }}
            >
              {beloved?.name || "N/A"}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.5 }}>
              {sunrise !== "N/A" && sunset !== "N/A"
                ? `${dob.replace(/-/g, "/")} - ${dod.replace(/-/g, "/")}`
                : "N/A"}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.3 }}>
              {details.gender || "N/A"}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={4} sx={{ mb: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              Nickname
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
              {details.nickname || "N/A"}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              Denomination
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
              {details.denomination || "N/A"}
            </Typography>
          </Box>
        </Stack>

        <Box>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            Religion
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
            {details.religion || "N/A"}
          </Typography>
        </Box>
      </Box>
    </>
  );
}

function ConferenceView({ bookingData }: { bookingData: BookingData }) {
  const cd = bookingData.conferenceDetails;
  const service = bookingData.services?.[0];

  const title     = cd?.conference_title    || service?.title || "N/A";
  const startDate = cd?.start_date_of_event || "N/A";
  const endDate   = cd?.end_date_of_event;
  const startTime = cd?.start_time          || service?.start_time;
  const endTime   = cd?.end_time            || service?.end_time;
  const venue     = cd?.venue_name          || service?.venue?.name;
  const city      = cd?.city                || service?.venue?.city;
  const audience  = cd?.expected_audience_size
    ? String(cd.expected_audience_size)
    : undefined;

  const timeRange =
    startTime && endTime ? `${startTime} – ${endTime}` : (startTime ?? "N/A");

  return (
    <>
      <SectionLabel>Conference Details</SectionLabel>

      <Box sx={{ bgcolor: "background.paper", borderRadius: "12px", p: 2, mb: 2 }}>
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: 18,
            color: "#0f172a",
            lineHeight: 1.2,
            textTransform: "uppercase",
            mb: 1,
          }}
        >
          {title}
        </Typography>

        <Stack direction="row" spacing={2}>
          <DateBlock label="START" value={startDate} />
          {endDate && endDate !== startDate && (
            <DateBlock label="END" value={endDate} />
          )}
        </Stack>
      </Box>

      <Stack spacing={0.5}>
        <InfoRow label="Time:"             value={timeRange} />
        <InfoRow label="Venue:"            value={venue     || "N/A"} />
        <InfoRow label="City:"             value={city      || "N/A"} />
        {audience && (
          <InfoRow label="Expected Guests:" value={audience} />
        )}
      </Stack>
    </>
  );
}

function ChurchView({ bookingData }: { bookingData: BookingData }) {
  const cd      = bookingData.churchDetails;
  const service = bookingData.services?.[0];

  const churchName = cd?.church_name             || service?.church_name || "N/A";
  const date       = cd?.date_of_service         || service?.date_of_service || "N/A";
  const startTime  = cd?.start_time              || service?.start_time;
  const endTime    = cd?.end_time                || service?.end_time;
  const preacher   = cd?.leader_preacher_name    || service?.leader_preacher_name;
  const venue      = service?.venue?.name;

  const timeRange =
    startTime && endTime ? `${startTime} – ${endTime}` : (startTime ?? "N/A");

  return (
    <>
      <SectionLabel>Church Service</SectionLabel>

      <Box sx={{ bgcolor: "background.paper", borderRadius: "12px", p: 2, mb: 2 }}>
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: 18,
            color: "#0f172a",
            lineHeight: 1.2,
            textTransform: "uppercase",
            mb: 1,
          }}
        >
          {churchName}
        </Typography>

        <Stack direction="row" spacing={2}>
          <DateBlock label="DATE" value={date} />
          <DateBlock label="TIME" value={timeRange} />
        </Stack>
      </Box>

      <Stack spacing={0.5}>
        {preacher && <InfoRow label="Preacher:" value={preacher} />}
        <InfoRow label="Venue:" value={venue || "N/A"} />
      </Stack>
    </>
  );
}

function OtherView({ bookingData }: { bookingData: BookingData }) {
  const cd      = bookingData.contentDetails;
  const service = bookingData.services?.[0];

  const title     = cd?.title      || service?.title || "N/A";
  const date      = cd?.date       || service?.date_of_service || "N/A";
  const startTime = cd?.start_time || service?.start_time;
  const endTime   = cd?.end_time   || service?.end_time;
  const host      = cd?.host_name;
  const guest     = cd?.guest_name;
  const venue     = service?.venue?.name;

  const timeRange =
    startTime && endTime ? `${startTime} – ${endTime}` : (startTime ?? "N/A");

  return (
    <>
      <SectionLabel>Event Details</SectionLabel>

      <Box sx={{ bgcolor: "background.paper", borderRadius: "12px", p: 2, mb: 2 }}>
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: 18,
            color: "#0f172a",
            lineHeight: 1.2,
            textTransform: "uppercase",
            mb: 1,
          }}
        >
          {title}
        </Typography>

        <Stack direction="row" spacing={2}>
          <DateBlock label="DATE" value={date} />
          <DateBlock label="TIME" value={timeRange} />
        </Stack>
      </Box>

      <Stack spacing={0.5}>
        {host  && <InfoRow label="Host:"  value={host}  />}
        {guest && <InfoRow label="Guest:" value={guest} />}
        <InfoRow label="Venue:" value={venue || "N/A"} />
      </Stack>
    </>
  );
}


export default function BookingInfoCard({ bookingData }: BookingInfoCardProps) {
  if (!bookingData) return null;

  const service = (bookingData.service ?? "").toLowerCase();

  const renderContent = () => {
    if (service === "conference") return <ConferenceView bookingData={bookingData} />;
    if (service === "church")     return <ChurchView     bookingData={bookingData} />;
    if (service === "other")      return <OtherView      bookingData={bookingData} />;
    return <FuneralView bookingData={bookingData} />;
  };

  return (
    <Paper sx={{ flex: 1, borderRadius: "12px", p: 2, boxShadow: 0, height: "100%" }}>
      {renderContent()}
    </Paper>
  );
}